import { AsyncPipe, DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Inject, Injector, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { IdtTrustHtmlPipe } from '@features/cms-render/ui/pipes/trust-html/trust-html.pipe';
import { configJson, getBaseUrl } from '@shared/navigation/store/navigation.selectors';

@Pipe({
    name: 'renderLinkTransformer',
})
export class LinkTransformer implements PipeTransform {
    private readonly asyncPipe: AsyncPipe;

    private readonly idTrushHtmlPipe: IdtTrustHtmlPipe;

    constructor(
        private readonly injector: Injector,
        private readonly store: Store,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        this.asyncPipe = new AsyncPipe(injector.get(ChangeDetectorRef));
        this.idTrushHtmlPipe = new IdtTrustHtmlPipe(injector.get(DomSanitizer));
    }

    @BrowserOnly<LinkTransformer, undefined>(undefined)
    public transform(html: string): string {
        const html$ = combineLatest([
            this.store.select(getStoreAndLanguage),
            this.store.select(configJson),
            this.store.select(getBaseUrl)]).pipe(
            filter(([storeAndLanguage, config, languageCode]) => !!storeAndLanguage && !!config && !!languageCode),
            map(([storeAndLanguage, config, languageCode]) => {
                const urlsFound: string[] = this.findUrlsInsideHtml(html);
                if (urlsFound.length === 0) return html;

                const urlFoundSections = urlsFound.map(urlFound => {
                    if (urlFound.includes('/section/') || urlFound.includes('?section=') || urlFound.includes('customSection') || urlFound.includes('customEvent')) {
                        // urls encontradas que son de tipo seccion o customSection o customEvent
                        return this.mapConfigJson(html, urlFound, config, languageCode);
                    }
                    return urlFound;
                });

                let _html = html;
                if (html.includes('customEvent')) {
                    _html = this.parseAndReplace(_html);
                } else {
                    for (let i = 0; i < urlsFound.length; i++) {
                        _html = _html.replace(urlsFound[i], urlFoundSections[i]); // reemplaza de customevent a url mapeada
                        _html = this.replaceUrlIfNeeded(_html, urlFoundSections[i], storeAndLanguage, languageCode); // corrige error de pais e idioma
                    }
                }

                const div = this.document.createElement('div');

                div.innerHTML = _html;
                Array.from(div.querySelectorAll('a')).forEach((el, i)=>{
                    el.setAttribute('cms-link', urlsFound[i]);
                });
                return div.innerHTML;
            })
        );

        return this.idTrushHtmlPipe.transform(
            this.asyncPipe.transform(html$)
        );
    }

    @BrowserOnly<LinkTransformer, undefined>(undefined)
    public parseAndReplace(htmlString: string): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        const aTags = doc.querySelectorAll('a');

        aTags.forEach(aTag => {
            const buttonEl = this.document.createElement('button');
            buttonEl.innerHTML = aTag.innerHTML; // Replace tags

            if (aTag.hasAttribute('href')) {
                buttonEl.setAttribute('data-link', aTag.getAttribute('href')); // Preserve link
                buttonEl.className = 'link-raw p-0';
            }

            aTag.parentNode.replaceChild(buttonEl, aTag);
        });

        return doc.body.innerHTML;
    }

    public findUrlsInsideHtml(htmlContent: string): string[] {
        const div: HTMLDivElement = this.document.createElement('div');
        div.innerHTML = htmlContent;
        const links: HTMLElement[] = Array.from(div.getElementsByTagName('a'));
        let urlsFound: any[] = [];
        for (let link of Array.from(links) as any) {
            urlsFound.push(link.getAttribute('href'));
        }
        return urlsFound;
    }

    public mapConfigJson(html, url, config, languageCode): string {
        const urlReplace = url?.includes('section')
            ? url.split('/').pop()
            : url.split('=').pop();
        return config[urlReplace]?.url ? `${languageCode}${config[urlReplace]?.url}` : `${languageCode}${urlReplace}`;
    }

    public replaceUrlIfNeeded(html, url, storeAndLanguage, languageCode): string {
        const splittedUrl: string[] = url.split('/');
        let possibleCountryPosition: number;
        let possibleLangPosition: number;

        // Get suposed path index for /country/lang inside URL
        if (url.includes('http')) { // absolute HTTP Cases
            possibleCountryPosition = 3;
            possibleLangPosition = 4;
        } else { // relative URL cases or non slash before path
            possibleCountryPosition = 1;
            possibleLangPosition = 2;
        }
        const possibleCountry = splittedUrl[possibleCountryPosition];
        const possibleLang: string = splittedUrl[possibleLangPosition];
        let urlReplaced;

        if (url.includes('http') && !url.includes('massimodutti')) {
            return html;
        }
        if (url.includes('mailto:') || url.includes('tel:') || url.startsWith('#')) {
            return html;
        }

        if (possibleLang) {
            if (possibleLang.toLocaleLowerCase() !== possibleCountry.toLowerCase()) {
                urlReplaced = url.replace(
                    possibleCountry + '/' + possibleLang,
                    possibleCountry.toLowerCase() + '/' + possibleLang.toLowerCase()
                );
            }
            if ( // Match duplicateds & special cases
                possibleLang === storeAndLanguage.countryCode.toLocaleLowerCase()
                || possibleLang === storeAndLanguage.countryCode
                || possibleLang === 'es' && storeAndLanguage.languageCode === 'es-mx'
                || possibleLang === 'id' && storeAndLanguage.languageCode === 'in'
            ) {
                // Replace match & apply lowerCase from original URL
                urlReplaced = url.replace(
                    possibleCountry + '/' + possibleLang,
                    possibleCountry.toLowerCase(),
                );
            }
            if (possibleLang === 'nl' && storeAndLanguage.defaultLanguage === 'en') {
                urlReplaced = url.replace(
                    possibleCountry + '/' + possibleLang,
                    possibleCountry.toLowerCase() + '/' + possibleLang.toLowerCase() + '-be',
                );
            }

            return html.replace(url, urlReplaced);
        }
        return html.replace(url, `${languageCode}${url}`);
    }
}
